import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { Headphones, Loader2, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { audioControllerHooks } from '@/manager'
import { AudioPlayState } from '@/manager/manager/audio-controller-manager'
import { ProgressSlider } from '@/components/common/progress-slider'
import { HintTooltip } from '../common/hint-tooltip'
import { HeadsetIcon } from './assets/headset-icon'
import { PlayRadiusIcon } from './assets/play-radius-icon'
import { PauseRadiusIcon } from './assets/pause-radius-icon'
import { XCircle2Icon } from './assets/x-circle2-icon'
import type { ScriptReplaceMark } from '@/services/make-generate'
import { AudioTTSCreateProps } from '@/services/audio-tts-create'
import { useTranslation } from 'react-i18next'

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

const iconBtnClass =
  'size-9 flex items-center justify-center rounded-[8px] hover:bg-[rgba(242,244,247,1)] transition-colors flex-shrink-0 cursor-pointer text-[rgba(16,24,40,1)] outline-none'

interface AuditionButtonProps {
  text: string;
  previewCredits?: number;
  voiceId?: string;
  marks?: ScriptReplaceMark[];
  speedRatio?: number;
  isAudioGenerating: boolean;
  readOnly?: boolean;
  managedAudioUrl?: string;
  onBeforeGenerate?: () => boolean;
  onGenerateAudio: (params: AudioTTSCreateProps) => Promise<string>;
}

/** 口播模式下：试听台词按钮 */
export const AuditionButton: React.FC<AuditionButtonProps> = ({
  text,
  previewCredits = 0,
  voiceId,
  marks = [],
  speedRatio = 1,
  isAudioGenerating,
  readOnly = false,
  managedAudioUrl,
  onGenerateAudio,
  onBeforeGenerate,
}) => {
  const { t } = useTranslation()
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [playerExpanded, setPlayerExpanded] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const mountedRef = useRef(true)

  const {
    playState,
    duration,
    currentTime,
    play,
    pause,
    stop,
    seekTo,
    startPlaybackRateDrag,
    endPlaybackRateDrag,
  } = audioControllerHooks.useController()

  const playStateRef = useRef(playState)
  playStateRef.current = playState

  // 音频生成提示文案
  const generateAudioTooltipText = t('live_script.audio_generate_point_tips', { point: previewCredits })
  // 重新生成音频提示文案
  const regenerateAudioTooltipText = t('live_script.audio_regenerate_point_tips', { point: previewCredits })

  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    // 没有音频 URL，则停止播放，并清空状态
    if (!managedAudioUrl) {
      stop()
      setAudioUrl(null)
      setPlayerExpanded(false)
      return
    }
    setAudioUrl(managedAudioUrl)
  }, [managedAudioUrl])


  // 生成音频预览参数
  const generateParams = useMemo(() => {
    const params: AudioTTSCreateProps = {
      text,
      voiceId,
      marks,
      point: previewCredits,
      speedRatio: speedRatio ? Number(speedRatio) : undefined,
    }
    return params
  }, [text, voiceId, marks, previewCredits, speedRatio])

  // 校验前置条件
  const validateBeforeGenerate = () => {
    const next = onBeforeGenerate?.() ?? true;
    if (!next || readOnly) return false;
    return true;
  }

  // 生成音频
  const handleHeadphoneClick = async () => {
    // 校验是否正在生成音频
    if (isAudioGenerating || isRegenerating) return

    if (audioUrl) {
      if (!playerExpanded) {
        setPlayerExpanded(true)
        await play(audioUrl)
      }
      return
    }

    try {
      // 校验前置条件
      if (!validateBeforeGenerate()) return

      const nextUrl = await onGenerateAudio(generateParams)
      if (!nextUrl || !mountedRef.current) return

      setAudioUrl(nextUrl)
      setPlayerExpanded(true)
      await play(nextUrl)
    } catch (error) {
      console.error('generate audio failed', error)
    }
  }

  // 重新生成音频
  const handleRegenerate = async (e: MouseEvent) => {
    e.stopPropagation()

    // 校验前置条件
    if (!validateBeforeGenerate()) return

    if (isAudioGenerating || isRegenerating) return
    setIsRegenerating(true)
    stop()
    try {
      const nextUrl = await onGenerateAudio(generateParams)
      if (!nextUrl || !mountedRef.current) return

      setAudioUrl(nextUrl)
      stop()
      try {
        await play(nextUrl)
      } catch {
        stop()
        await play(nextUrl)
      }
      if (!mountedRef.current) return
      const deadline = Date.now() + 15000
      while (Date.now() < deadline && mountedRef.current) {
        const s = playStateRef.current
        if (s === AudioPlayState.PLAYING || s === AudioPlayState.ERROR) break
        await sleep(40)
      }
      if (mountedRef.current && playStateRef.current === AudioPlayState.PLAYING) {
        setPlayerExpanded(true)
      }
    } finally {
      if (mountedRef.current) setIsRegenerating(false)
    }
  }

  // 播放音频
  const handleTogglePlay = async (e: MouseEvent) => {
    e.stopPropagation()
    if (!audioUrl) return
    if (playState === AudioPlayState.PLAYING) {
      pause()
    } else {
      await play(audioUrl)
    }
  }

  // 关闭播放器
  const handleClosePlayer = (e: MouseEvent) => {
    e.stopPropagation()
    stop()
    setPlayerExpanded(false)
  }

  // 重新生成音频按钮
  const regenerateTrigger = (
    <button
      type="button"
      aria-label="重新生成音频预览"
      disabled={isRegenerating || isAudioGenerating || readOnly}
      onClick={handleRegenerate}
      className={cn(
        iconBtnClass,
        (isRegenerating || isAudioGenerating || readOnly) && 'pointer-events-none opacity-70',
        !text.trim() && 'cursor-not-allowed'
      )}
    >
      <RefreshCw
        className={cn(
          'size-5',
          isRegenerating && 'animate-spin',
          !text.trim() && 'opacity-50'
        )}
        aria-hidden
      />
    </button>
  )

  // 音频生成中
  if (isAudioGenerating) {
    return (
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          type="button"
          disabled
          aria-busy
          aria-label="生成中"
          className={cn(iconBtnClass, 'pointer-events-none cursor-default hover:bg-transparent')}
        >
          <Loader2 className="size-5 animate-spin shrink-0" aria-hidden />
        </button>
      </div>
    )
  }

  // 没有音频 URL，则显示生成音频预览按钮
  if (!audioUrl) {
    return (
      <HintTooltip
        text={generateAudioTooltipText}
        trigger={
          <button
            aria-label="生成音频预览"
            onClick={handleHeadphoneClick}
            disabled={readOnly}
            className={cn(
              iconBtnClass,
              !text.trim() && 'opacity-50 cursor-not-allowed',
              readOnly && 'pointer-events-none opacity-70'
            )}
          >
            <HeadsetIcon className="size-5" aria-hidden />
          </button>
        }
      />
    )
  }

  // 没有播放器，则显示音频预览按钮
  if (!playerExpanded) {
    return (
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <HintTooltip
          text={t('live_script.audio_preview')}
          trigger={
            <button type="button" aria-label="音频预览" onClick={handleHeadphoneClick} className={iconBtnClass}>
              <Headphones className="size-5" aria-hidden />
            </button>
          }
        />
        <HintTooltip text={regenerateAudioTooltipText} trigger={regenerateTrigger} />
      </div>
    )
  }

  const isPlaying = playState === AudioPlayState.PLAYING
  const sliderMax = Math.max(duration, 0.01)

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0 min-w-0 max-w-[min(100%,320px)]">
      <button
        type="button"
        aria-label={isPlaying ? '暂停' : '播放'}
        onClick={handleTogglePlay}
        className={iconBtnClass}
      >
        {isPlaying ? (
          <PauseRadiusIcon className="" />
        ) : (
          <PlayRadiusIcon className="" />
        )}
      </button>
      <div className="mx-1 mr-2">
        <ProgressSlider
          value={currentTime}
          max={sliderMax}
          trackColor="rgb(234,236,240)"
          progressColor="rgb(28,36,51)"
          thumbColor="rgb(255,255,255)"
          thumbBorderColor="rgb(28,36,51)"
          alwaysShowThumb
          onChange={seekTo}
          onDragStart={startPlaybackRateDrag}
          onDragEnd={endPlaybackRateDrag}
          className="flex-1 min-w-[72px]"
        />
      </div>
      <div onClick={handleClosePlayer} className="cursor-pointer">
        <XCircle2Icon className="size-5 text-[rgba(208,213,221,1)] hover:text-[#b9babd] transition-colors" />
      </div>
      <HintTooltip text={regenerateAudioTooltipText} trigger={regenerateTrigger} />
    </div>
  )
}
